import { useEffect, useRef, useState } from 'react';
import { ToolForm, ToolRunStatus, EvidencePackDownloads } from '../pages/tools/ToolComponents';
import { buildApiUrl } from '../lib/apiBase';
import { GuestConnectGitHub } from './GuestConnectGitHub';
import { GuestRepoPicker, GuestRepository } from './GuestRepoPicker';
import { GuestUpgradeCta, GuestUpgradeCtaData } from './GuestUpgradeCta';

interface GuestSessionDto {
  sessionId: string;
  expiresAt: string;
  hasGitHubConnection: boolean;
  selectedRepo: { id: number; fullName: string } | null;
  hasUsedReport: boolean;
  limitReached: boolean;
}

interface GuestToolPanelProps {
  toolKey: 'pr-approvals' | 'branch-protection';
  toolLabel: string;
  description?: string;
}

type RunStatus = 'idle' | 'generating' | 'completed' | 'error';

export function GuestToolPanel({ toolKey, toolLabel, description }: GuestToolPanelProps) {
  const [session, setSession] = useState<GuestSessionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [status, setStatus] = useState<RunStatus>('idle');
  const [packId, setPackId] = useState('');
  const [checksum, setChecksum] = useState('');
  const [error, setError] = useState('');
  const [upgradeCta, setUpgradeCta] = useState<GuestUpgradeCtaData | null>(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [selectedRepoId, setSelectedRepoId] = useState<number | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const loadSession = async (options?: { silent?: boolean }) => {
    try {
      const silent = options?.silent === true;
      if (!silent) {
        setLoading(true);
        setSessionError(null);
      }

      let response = await fetch(buildApiUrl('/api/v1/guest/session'), { credentials: 'include' });
      if (response.status === 404) {
        const startResponse = await fetch(buildApiUrl('/api/v1/guest/start'), {
          method: 'POST',
          credentials: 'include',
        });
        if (!startResponse.ok) {
          console.error('Failed to start guest session:', startResponse.status, await startResponse.text());
          throw new Error('Failed to start guest session');
        }
        // Log the Set-Cookie header presence (will be empty due to httpOnly, but useful for debugging)
        console.log('Guest session started, response status:', startResponse.status);
        response = await fetch(buildApiUrl('/api/v1/guest/session'), { credentials: 'include' });
        console.log('Guest session check after start:', response.status);
      }

      if (!response.ok) {
        throw new Error('Failed to load guest session');
      }

      const data = await response.json();
      setSession(data);
      setSelectedRepoId(data.selectedRepo?.id ?? null);

      if (data.limitReached) {
        await loadUpgradeCta();
      }
    } catch (err) {
      if (!options?.silent) {
        setSessionError(err instanceof Error ? err.message : 'Failed to load guest session');
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  const loadUpgradeCta = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/v1/guest/upgrade-cta'), { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      if (data?.cta) {
        setUpgradeCta(data.cta);
      }
    } catch {
      // Ignore CTA fetch errors
    }
  };

  useEffect(() => {
    void loadSession();
    return () => stopPolling();
  }, []);

  const pollPackStatus = async (id: string) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/guest/evidence-packs/${id}`), { credentials: 'include' });
      if (!response.ok) return;

      const pack = await response.json();
      if (pack.status === 'Ready') {
        setChecksum(pack.checksum || '');
        setStatus('completed');
        setProgressMessage('');
        stopPolling();
      } else if (pack.status === 'Failed') {
        setError(pack.errorMessage || 'Generation failed');
        setStatus('error');
        setProgressMessage('');
        stopPolling();
      } else {
        setProgressMessage('Generating...');
      }
    } catch {
      // Ignore polling errors
    }
  };

  useEffect(() => {
    if (packId && status === 'generating') {
      pollingIntervalRef.current = window.setInterval(() => {
        void pollPackStatus(packId);
      }, 2000);
    }

    return () => stopPolling();
  }, [packId, status]);

  const handleRepositorySelect = async (repo: GuestRepository) => {
    try {
      setError('');
      const response = await fetch(buildApiUrl('/api/v1/guest/repository/select'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ repositoryId: repo.id }),
      });

      if (!response.ok) {
        throw new Error('Failed to select repository');
      }

      setSelectedRepoId(repo.id);
      setSession((prev) => (prev ? { ...prev, selectedRepo: { id: repo.id, fullName: repo.fullName } } : prev));
      await loadSession({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select repository');
    }
  };

  const handleGenerate = async (_data: Record<string, FormDataEntryValue>) => {
    if (!selectedRepoId) {
      setError('Please select a repository');
      setStatus('error');
      return;
    }

    setStatus('generating');
    setError('');
    setProgressMessage('Starting report generation...');

    try {
      const response = await fetch(buildApiUrl(`/api/v1/guest/tools/${toolKey}/generate`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (response.status === 429) {
        const data = await response.json();
        setStatus('error');
        setError(data.message || 'Guest limit reached');
        if (data.upgradeCta) {
          setUpgradeCta(data.upgradeCta);
        }
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const data = await response.json();
      setPackId(data.packId);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  // Render loading or error state inline (not as full replacement)
  const renderContent = () => {
    if (loading) {
      return (
        <div className="border border-slate-700 rounded-lg p-6 bg-slate-800">
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <div className="animate-spin h-5 w-5 text-blue-500"></div>
            <span>Loading guest session...</span>
          </div>
        </div>
      );
    }

    if (sessionError) {
      return (
        <div className="border border-red-200 rounded-lg p-6 bg-red-50 text-red-900">
          <p className="font-semibold">Unable to load guest session</p>
          <p className="text-sm mt-1">{sessionError}</p>
          <button
            onClick={() => void loadSession()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
          >
            Retry
          </button>
        </div>
      );
    }

    if (!session) {
      return null;
    }

    if (session.limitReached) {
      return <GuestUpgradeCta cta={upgradeCta} />;
    }

    if (!session.hasGitHubConnection) {
      return (
        <>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900 mb-6">
            <p className="font-semibold">Guest mode</p>
            <p className="text-sm mt-1">Connect GitHub to run a single guest report for one repository.</p>
          </div>
          <GuestConnectGitHub onConnected={() => void loadSession()} />
        </>
      );
    }

    return (
      <>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 mb-6">
          <p className="font-semibold">Guest mode: one report, one repository</p>
          <p className="text-sm mt-1">{description || `Generate a single ${toolLabel} report before creating an account.`}</p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Select Repository *
          </label>
          <GuestRepoPicker selectedRepoId={selectedRepoId} onSelect={handleRepositorySelect} />
        </div>

        <ToolForm onSubmit={handleGenerate} isLoading={status === 'generating'}>
          <div className="text-sm text-gray-600">
            This guest report uses default settings for speed and compliance-friendly output.
          </div>
        </ToolForm>

        <ToolRunStatus
          status={status}
          message={status === 'generating' ? (progressMessage || 'Generating report...') : undefined}
          error={error}
        />

        {status === 'completed' && packId && (
          <EvidencePackDownloads
            packId={packId}
            checksum={checksum}
            formats={['pdf']}
            onDownload={(format) => {
              window.location.href = `${buildApiUrl(`/api/v1/guest/evidence-packs/${packId}/download`)}?format=${format}`;
            }}
            showShareButton={false}
          />
        )}

        {status === 'error' && upgradeCta && <GuestUpgradeCta cta={upgradeCta} />}
      </>
    );
  };

  return renderContent();
}
